import DefaultGallery from "./DefaultGallery"
import EditorialLuxeGallery from "./EditorialLuxeGallery"
import SoftRomanticGallery from "./SoftRomanticGallery"
import CinematicGallery from "./CinematicGallery"
import CorporateGallery from "./CorporateGallery"
import SportsGallery from "./SportsGallery"
import { GalleryDesignComponent } from "./types"

export type ColorMode = "light" | "dark"

/**
 * Map of galleryTheme design keys -> design component.
 * The color mode (light/dark) is encoded as a suffix on the theme name
 * (e.g. "editorial-luxe-dark") and handled separately by useTheme.
 *
 * To add a design: build the component, then register its key here.
 */
const designs: Record<string, GalleryDesignComponent> = {
  default: DefaultGallery,
  "editorial-luxe": EditorialLuxeGallery,
  "soft-romantic": SoftRomanticGallery,
  cinematic: CinematicGallery,
  corporate: CorporateGallery,
  sports: SportsGallery,
}

/**
 * Splits a galleryTheme value into its design key and color mode.
 * "editorial-luxe-dark" -> { designKey: "editorial-luxe", mode: "dark" }
 * "default-dark"        -> { designKey: "default", mode: "dark" }
 * undefined / ""        -> { designKey: "default", mode: "light" }
 */
export const parseGalleryTheme = (
  galleryTheme?: string | null
): { designKey: string; mode: ColorMode } => {
  const raw = (galleryTheme || "").toLowerCase().trim()
  const mode: ColorMode = raw.endsWith("-dark") ? "dark" : "light"
  const designKey = raw.replace(/-(dark|light)$/, "") || "default"
  return { designKey, mode }
}

/** Resolves the design component for a galleryTheme, falling back to default. */
export const resolveGalleryDesign = (
  galleryTheme?: string | null
): GalleryDesignComponent => {
  const { designKey } = parseGalleryTheme(galleryTheme)
  return designs[designKey] || designs.default
}

/**
 * The base/UI font class for each design, applied to the whole gallery view
 * (including shared chrome like the header, selection bar and bottom bar) so
 * typography is consistent. Empty string keeps the app default (Inter).
 * Headings within a design can still override with their own font class.
 */
const designFontClass: Record<string, string> = {
  default: "",
  "editorial-luxe": "font-hanken",
  "soft-romantic": "font-ebgaramond",
  cinematic: "font-archivo",
  corporate: "font-archivo",
  sports: "font-archivo",
}

export const getDesignFontClass = (galleryTheme?: string | null): string => {
  const { designKey } = parseGalleryTheme(galleryTheme)
  return designFontClass[designKey] || ""
}

/**
 * The display/heading font for each design (e.g. serif for editorial), used
 * for prominent text like the About quote. Empty string keeps the default.
 */
const designHeadingFontClass: Record<string, string> = {
  default: "",
  "editorial-luxe": "font-cormorant",
  "soft-romantic": "font-ebgaramond",
  cinematic: "font-bodoni",
  corporate: "font-spacegrotesk",
  sports: "font-anton",
}

export const getDesignHeadingFontClass = (
  galleryTheme?: string | null
): string => {
  const { designKey } = parseGalleryTheme(galleryTheme)
  return designHeadingFontClass[designKey] || ""
}

/**
 * Casing/letter-spacing for chrome labels (nav links, folder tabs) so they
 * match each design's typographic style. Empty string keeps the default
 * (the components' own `capitalize`).
 */
const designLabelClass: Record<string, string> = {
  default: "",
  "editorial-luxe": "uppercase tracking-[0.12em] text-[11px]",
  cinematic: "uppercase tracking-[0.14em] text-[11px]",
  corporate: "uppercase tracking-[0.16em] text-[11px]",
  sports: "uppercase tracking-[0.18em] text-[11px] font-anton",
}

export const getDesignLabelClass = (galleryTheme?: string | null): string => {
  const { designKey } = parseGalleryTheme(galleryTheme)
  return designLabelClass[designKey] || ""
}

/**
 * Just the casing/letter-spacing of a design (no font size), for elements that
 * keep their own size but should match the design's case — e.g. modal titles
 * and buttons.
 */
const designCasingClass: Record<string, string> = {
  default: "",
  "editorial-luxe": "uppercase tracking-[0.12em]",
  cinematic: "uppercase tracking-[0.14em]",
  corporate: "uppercase tracking-[0.16em]",
  sports: "uppercase tracking-[0.18em]",
}

export const getDesignCasingClass = (galleryTheme?: string | null): string => {
  const { designKey } = parseGalleryTheme(galleryTheme)
  return designCasingClass[designKey] || ""
}

/**
 * Per-design font sizes for modal title/buttons. Uses `!` so it overrides the
 * shared popupModalStyle base sizes; empty keeps the default sizes.
 */
const designModalSize: Record<
  string,
  { title: string; desc: string; button: string }
> = {
  default: { title: "", desc: "", button: "" },
  "editorial-luxe": { title: "!text-base md:!text-lg", desc: "", button: "!text-[11px]" },
  // EB Garamond reads small, so bump the modal text up.
  "soft-romantic": {
    title: "!text-xl md:!text-[28px]",
    desc: "!text-base md:!text-lg",
    button: "!text-base md:!text-lg",
  },
}

export const getDesignModalSize = (
  galleryTheme?: string | null
): { title: string; desc: string; button: string } => {
  const { designKey } = parseGalleryTheme(galleryTheme)
  return designModalSize[designKey] || designModalSize.default
}
